// EVERY LINK LANDS SIGNED IN — what the portal remembers between tabs so a
// link never asks someone who is already signed in to sign in again.
//
// The platform rule, Chauncey White 2026-10-23: "EVERY link emailed or sent or
// coded should check to see if the user is logged in.... its beyond
// exhausting having to authenticate EVERYTIME"; and 2026-11-02: "no matter
// waht app i go to, i am forced to Authenticate. there is no token stored?".
// Ported from ContractWISE (aims-contracts c9c94be, itself ExpenseWISE
// 0524e05 + Helpdesk c188942 and the fleet's review findings): the link's
// person beats a cached account, the hint leaves the address bar once signed
// in, and a sign-out counts as a CHOICE only when the menu made it. Outbound
// email goes through the shared Helpdesk sender (POST /api/internal/email),
// which names each recipient in every WISE link centrally — so there is no
// outbound half here.
//
// The MSAL cache itself is localStorage now (msalConfig.ts): a link opens a
// NEW tab, and sessionStorage belonged to one tab. Beside it, this keeps:
//   * who last signed in on this browser (localStorage) — the hint for the
//     automatic sign-in when a link carries none of its own;
//   * that they chose Sign out (localStorage) — never signed straight back in;
//   * that the one automatic redirect of this TAB was tried (sessionStorage,
//     the key SignInGate always used) — so a failed one cannot loop.
//
// The link's own hint is read by linkHint.ts — in the query, or inside the
// hash. This module is pure on purpose (no window at import), so
// portal/tests drives it over fake stores.

export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export const LAST_UPN_KEY = 'itms-qc:last_upn';
export const SIGNED_OUT_KEY = 'itms-qc:signed_out';
/** One automatic sign-in redirect per tab; cleared when a sign-in succeeds.
 *  The key SignInGate used before this module, kept. */
export const SSO_ATTEMPTED_KEY = 'wise_sso_attempted';

function read(store: KeyValueStore | null, key: string): string | null {
  try {
    return store?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function write(store: KeyValueStore | null, key: string, value: string | null): void {
  try {
    if (!store) return;
    if (value === null) store.removeItem(key);
    else store.setItem(key, value);
  } catch {
    // Storage switched off: the portal still works, it just asks more often.
  }
}

function browserStore(which: 'localStorage' | 'sessionStorage'): KeyValueStore | null {
  try {
    return typeof window === 'undefined' ? null : (window[which] ?? null);
  } catch {
    return null;
  }
}

export interface SsoStores {
  local: KeyValueStore | null;
  session: KeyValueStore | null;
}

const browserStores = (): SsoStores => ({
  local: browserStore('localStorage'),
  session: browserStore('sessionStorage'),
});

/** A sign-in (or a token) succeeded: remember who, forget a chosen sign-out,
 *  and re-arm this tab's automatic sign-in. */
export function rememberSignedIn(upn: string | null | undefined, stores: SsoStores = browserStores()): void {
  if (upn) write(stores.local, LAST_UPN_KEY, upn);
  write(stores.local, SIGNED_OUT_KEY, null);
  write(stores.session, SSO_ATTEMPTED_KEY, null);
}

/** The person chose Sign out (the menu) — do not sign them straight back in. */
export function markSignedOut(stores: SsoStores = browserStores()): void {
  write(stores.local, SIGNED_OUT_KEY, '1');
}

export function signedOutOnPurpose(stores: SsoStores = browserStores()): boolean {
  return read(stores.local, SIGNED_OUT_KEY) === '1';
}

/** Who to sign in on arrival: the link's own hint (linkHint.ts), else whoever
 *  last signed in on this browser, else nobody — Entra then shows its own
 *  picker. */
export function arrivalLoginHint(linkHint: string | null, stores: SsoStores = browserStores()): string | undefined {
  return linkHint?.trim() || read(stores.local, LAST_UPN_KEY) || undefined;
}

/** True once per tab: claim the one automatic sign-in redirect. */
export function claimAutoSignIn(stores: SsoStores = browserStores()): boolean {
  if (read(stores.session, SSO_ATTEMPTED_KEY)) return false;
  write(stores.session, SSO_ATTEMPTED_KEY, '1');
  return true;
}

/** Whether a tab with nobody signed in goes straight to Entra: not after a
 *  chosen sign-out, and not twice in one tab. */
export function shouldSignInAutomatically(stores: SsoStores = browserStores()): boolean {
  return !signedOutOnPurpose(stores) && read(stores.session, SSO_ATTEMPTED_KEY) === null;
}

/**
 * A link names its person; the browser may hold someone else. With the cache
 * in localStorage a shared PC still holds the last person who did not sign
 * out, and the page would open as THEM — a change made under their name. So: the cached account the link names, if there is one;
 * otherwise, when other accounts are cached, sign in as the named person. No
 * hint, or nobody cached (SignInGate does that), changes nothing.
 */
export function linkAccount<T extends { username: string }>(
  accounts: readonly T[],
  linkHint: string | null,
): { account: T | null; signInAs: string | null } {
  if (linkHint === null || linkHint.trim() === '' || accounts.length === 0) return { account: null, signInAs: null };
  const wanted = linkHint.trim().toLowerCase();
  const match = accounts.find((a) => a.username.toLowerCase() === wanted) ?? null;
  return match !== null ? { account: match, signInAs: null } : { account: null, signInAs: linkHint.trim() };
}

/**
 * The account this tab acts as: the ACTIVE one (the link's person, or whoever
 * just signed in), else the first cached. Every reader goes through here — a
 * bare `accounts[0]` on a shared PC is the last person who did not sign out.
 */
export function signedInAccount<T>(instance: { getActiveAccount(): T | null }, accounts: readonly T[]): T | undefined {
  return instance.getActiveAccount() ?? accounts[0];
}
